import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, 
  Typography, 
  TextField, 
  MenuItem, 
  Button, 
  IconButton,
  Switch, 
  FormControlLabel,
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import api from 'state/axios';
import { formatDate } from 'state/helpers';
import CustomDatePicker from 'components/CustomDatePicker';
import FlexBetween from 'components/FlexBetween';
import SectionLabel from 'components/SectionLabel';
import FieldDisplay from 'components/FieldDisplay';


const CONTRACT_TYPES = ['Fixed-Term', 'Indefinite-Term', 'Probation'];

const jobTitleDisplay = (jt) =>
  Array.isArray(jt.jobTitle) ? jt.jobTitle.join(', ') : (jt.jobTitle || '-');


const CrewEmployment = ({ values, handleChange, handleBlur, errors, touched, setFieldValue, isEditMode }) => {
  const crewRanks = useSelector((state) => state.crewRanks);
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [deptUnits, setDeptUnits] = useState([]);
  const [jobGrades, setJobGrades] = useState([]);
  const [defJobTitles, setDefJobTitles] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/divisions/active'),
      api.get('/departments/active'),
      api.get('/deptunits/active'),
      api.get('/jobgrades/active'),
      api.get('/jobtitles/active'),
    ]).then(([divRes, deptRes, duRes, jgRes, jtRes]) => {
      setDivisions(divRes.data || []);
      setDepartments(deptRes.data || []);
      setDeptUnits(duRes.data || []);
      setJobGrades(jgRes.data || []);
      setDefJobTitles(jtRes.data || []);
    }).catch(() => {});
  }, []);


  if (!isEditMode) return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Rank" value={values.rank} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Division" value={values.division} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Department" value={values.department} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Dept Unit" value={values.deptUnit} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Agency" value={values.agency} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
        <FieldDisplay label="Employment Date" value={formatDate(values.employmentDate)} />
      </Box>
      <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 6' } }}>
        <FieldDisplay label="Active" value={values.isActive ? 'Yes' : 'No'} />
      </Box>

      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Contract</SectionLabel>
        <Box 
          display="grid" 
          gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
          gap={2} 
        >
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Contract Type" value={values.employmentContract} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Job Grade" value={values.jobGradeCode} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="Start Date" value={formatDate(values.employmentStartDate)} />
          </Box>
          <Box sx={{ gridColumn: { xs: 'span 6', sm: 'span 3' } }}>
            <FieldDisplay label="End Date" value={formatDate(values.employmentEndDate)} />
          </Box>          
        </Box>
      </Box>
      
      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <SectionLabel>Job Titles</SectionLabel>
        {!values.jobTitles?.length ? (
          <Typography variant="body2" color="text.secondary">
            No job titles recorded.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>JobTitle</TableCell>
                  <TableCell>EffectiveFrom</TableCell>
                  <TableCell>EffectiveTo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {values.jobTitles.map((jt, i) => (
                  <TableRow key={i}>
                    <TableCell>{jobTitleDisplay(jt)}</TableCell>
                    <TableCell>{formatDate(jt.effectiveFrom)}</TableCell>
                    <TableCell>{jt.effectiveTo ? formatDate(jt.effectiveTo) : 'Present'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );


  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
      gap={2}
    >
      <TextField 
        fullWidth 
        select size="small" 
        label="Rank" 
        name="rank"
        value={values.rank || ''} 
        onChange={handleChange} 
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {crewRanks.map(r => (
          <MenuItem key={r._id} value={r.rankCode}>
            {r.rankCode} - {r.description}
          </MenuItem>
        ))}
      </TextField>
      
      <TextField
        fullWidth
        select
        size="small"
        label="Division"
        name="division"
        value={values.division || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {divisions.map(d => (
          <MenuItem key={d.divisionCode} value={d.divisionCode}>
            {d.divisionCode} - {d.description}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        select
        size="small"
        label="Department"
        name="department"
        value={values.department || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {departments.map(d => (
          <MenuItem key={d.departmentCode} value={d.departmentCode}>
            {d.departmentCode} - {d.description}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        select
        size="small"
        label="Dept Unit"
        name="deptUnit"
        value={values.deptUnit || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {deptUnits
          .filter(d => !values.department || d.departmentCode === values.department)
          .map(d => (
            <MenuItem key={d.deptUnitCode} value={d.deptUnitCode}>
              {d.deptUnitCode} - {d.description}
            </MenuItem>
          ))}
      </TextField>
      <TextField 
        fullWidth 
        size="small" 
        label="Agency" 
        name="agency"
        value={values.agency} 
        onChange={handleChange} 
        onBlur={handleBlur}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="Employment Date" 
        name="employmentDate"
        value={values.employmentDate ? new Date(values.employmentDate) : null}
        onChange={setFieldValue} 
        touched={touched} 
        errors={errors}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gridColumn: { xs: 'span 12', sm: 'span 3' }, 
        }}
      >
        <FormControlLabel
          control={
            <Switch 
              checked={values.isActive} 
              onChange={(e) => setFieldValue('isActive', e.target.checked)} 
            />
          }
          label="Active"
        />
      </Box>

      <SectionLabel>Contract</SectionLabel>
      <TextField 
        fullWidth 
        select 
        size="small" 
        label="Contract Type" 
        name="employmentContract"
        value={values.employmentContract || ''} 
        onChange={handleChange} 
        onBlur={handleBlur}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {CONTRACT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>
      
      <TextField 
        fullWidth 
        select 
        size="small" 
        label="Job Grade"
        name="jobGradeCode"
        value={values.jobGradeCode || ''}
        onChange={(e) => setFieldValue('jobGradeCode', e.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      >
        <MenuItem value=""><em>None</em></MenuItem>
        {jobGrades.map(j => (
          <MenuItem key={j.jobGradeCode} value={j.jobGradeCode}>
            {j.jobGradeCode}
          </MenuItem>
        ))}
      </TextField>

      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="Start Date" 
        name="employmentStartDate"
        value={values.employmentStartDate ? new Date(values.employmentStartDate) : null}
        onChange={setFieldValue} 
        touched={touched} 
        errors={errors}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      <CustomDatePicker 
        variant="outlined"
        size="small" 
        label="End Date" 
        name="employmentEndDate"
        value={values.employmentEndDate ? new Date(values.employmentEndDate) : null}
        onChange={setFieldValue} 
        touched={touched} 
        errors={errors}
        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
      />
      
      {/* Job Titles */}
      <Box 
        display="flex" 
        flexDirection="column" 
        gap={1} 
        sx={{ gridColumn: 'span 12' }}
      >
        <FlexBetween>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Job Titles
          </Typography>
          <Button 
            size="small" 
            startIcon={<Add />}
            onClick={() => setFieldValue('jobTitles', [
              ...values.jobTitles,
              { jobTitle: [], effectiveFrom: new Date(), effectiveTo: null },
            ])}
          >
            Add Job Title
          </Button>
        </FlexBetween>
        {values.jobTitles.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No job titles recorded.
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {values.jobTitles.map((jt, i) => {
              const update = (field, val) => {
                const next = [...values.jobTitles];
                next[i] = { ...next[i], [field]: val };
                setFieldValue('jobTitles', next);
              };
              return (
                <Box 
                  key={i} 
                  display="flex" 
                  alignItems="center" 
                  gap={2}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                >
                  <Box 
                    flex={1}
                    display="grid" 
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))" 
                    gap={2} 
                  >
                    <TextField 
                      fullWidth 
                      select 
                      size="small" 
                      label="Job Title"
                      value={Array.isArray(jt.jobTitle) ? jt.jobTitle : (jt.jobTitle ? [jt.jobTitle] : [])}
                      onChange={(e) => update('jobTitle', e.target.value)}
                      slotProps={{ select: { multiple: true }, inputLabel: { shrink: true } }}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {defJobTitles.map(j => (
                        <MenuItem key={j.jobTitleCode} value={j.jobTitleCode}>
                          {j.jobTitleCode} - {j.description}
                        </MenuItem>
                      ))}
                    </TextField>
                    <CustomDatePicker 
                      variant="outlined" 
                      size="small" 
                      label="Effective From" 
                      name={`jobTitles[${i}].effectiveFrom`}
                      value={jt.effectiveFrom ? new Date(jt.effectiveFrom) : null}
                      onChange={(_, val) => update('effectiveFrom', val)}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                    />
                    <CustomDatePicker 
                      variant="outlined" 
                      size="small" 
                      label="Effective To" 
                      name={`jobTitles[${i}].effectiveTo`}
                      value={jt.effectiveTo ? new Date(jt.effectiveTo) : null}
                      onChange={(_, val) => update('effectiveTo', val)}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 3' } }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-end">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => setFieldValue('jobTitles', values.jobTitles.filter((_, idx) => idx !== i))}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CrewEmployment;
